// XP and Gamification utilities for ShowMeTrade

export interface LevelInfo {
  level: number;
  xp: number;
  xpForCurrentLevel: number;
  xpForNextLevel: number;
  progress: number; // 0-100
  rankTitle: string;
  rankColor: string;
  rankGlow: string;
}

// XP thresholds per level
const LEVEL_THRESHOLDS: Record<number, number> = {
  1: 0,
  2: 20,
  3: 50,
  4: 100,
  5: 200,
  6: 350,
  7: 500,
  8: 700,
  9: 950,
  10: 1200,
  15: 1900,
  20: 2800,
  25: 4000,
  30: 5500,
  35: 7500,
  40: 10000,
  45: 13000,
  50: 17000,
};

// Sorted level entries
const SORTED_LEVELS = Object.entries(LEVEL_THRESHOLDS)
  .map(([l, xp]) => ({ level: parseInt(l), xp }))
  .sort((a, b) => a.level - b.level);

export function getLevelFromXP(xp: number): number {
  let level = 1;
  for (const entry of SORTED_LEVELS) {
    if (xp >= entry.xp) level = entry.level;
    else break;
  }
  return level;
}

export function getLevelInfo(xp: number): LevelInfo {
  let level = getLevelFromXP(xp);

  // Find current and next level thresholds
  const currentEntry = SORTED_LEVELS.find((e) => e.level === level);
  const nextEntry = SORTED_LEVELS.find((e) => e.level > level);

  const xpForCurrentLevel = currentEntry?.xp ?? 0;
  const xpForNextLevel = nextEntry?.xp ?? xpForCurrentLevel + 500;

  const xpInLevel = xp - xpForCurrentLevel;
  const xpNeeded = xpForNextLevel - xpForCurrentLevel;
  const progress = Math.min(100, Math.round((xpInLevel / xpNeeded) * 100));

  return {
    level,
    xp,
    xpForCurrentLevel,
    xpForNextLevel,
    progress,
    rankTitle: getRankTitle(level),
    rankColor: getRankColor(level),
    rankGlow: getRankGlow(level),
  };
}

export function getRankTitle(level: number): string {
  if (level >= 50) return 'Market Master';
  if (level >= 31) return 'Elite';
  if (level >= 16) return 'Pro Trader';
  if (level >= 6) return 'Trader';
  return 'Beginner';
}

export function getRankColor(level: number): string {
  if (level >= 50) return '#b07c1f'; // gold
  if (level >= 31) return '#0c5b41'; // forest green (accent)
  if (level >= 16) return '#3e5fbf'; // blue
  if (level >= 6) return '#0e8a4d';  // green
  return '#8c9891'; // gray
}

export function getRankGlow(level: number): string {
  if (level >= 50) return '0 0 8px rgba(176,124,31,0.5)';
  if (level >= 31) return '0 0 8px rgba(12,91,65,0.4)';
  if (level >= 16) return '0 0 6px rgba(62,95,191,0.4)';
  if (level >= 6) return '0 0 4px rgba(14,138,77,0.3)';
  return 'none';
}

export function getRankBadgeStyle(level: number): { bg: string; color: string; border: string } {
  if (level >= 50) return { bg: 'rgba(176,124,31,0.15)', color: '#b07c1f', border: 'rgba(176,124,31,0.4)' };
  if (level >= 31) return { bg: 'rgba(12,91,65,0.15)', color: '#0c5b41', border: 'rgba(12,91,65,0.4)' };
  if (level >= 16) return { bg: 'rgba(62,95,191,0.15)', color: '#3e5fbf', border: 'rgba(62,95,191,0.4)' };
  if (level >= 6) return { bg: 'rgba(14,138,77,0.12)', color: '#0e8a4d', border: 'rgba(14,138,77,0.35)' };
  return { bg: 'rgba(140,152,145,0.12)', color: '#63736b', border: 'rgba(140,152,145,0.3)' };
}

// XP reward amounts
export const XP_REWARDS = {
  POST_TRADE: 10,
  WIN_TRADE: 25,
  LOSS_TRADE: 5,
  LIKE_RECEIVED: 1,
  COMMENT_RECEIVED: 2,
  FOLLOWER_GAINED: 5,
  BATTLE_WIN: 20,
  DAILY_LOGIN: 5,
} as const;

// Achievement definitions
export interface AchievementDef {
  id: string;
  name: string;
  description: string;
  icon: string;
  xpReward: number;
}

export const ACHIEVEMENTS: AchievementDef[] = [
  { id: 'first_trade',    name: 'First Trade',      description: 'Posted your first trade idea',      icon: '🚀', xpReward: 10 },
  { id: 'first_win',      name: 'First Win',         description: 'Closed your first winning trade',   icon: '🏆', xpReward: 25 },
  { id: 'five_wins',      name: '5 Wins',            description: 'Closed 5 winning trades',           icon: '⭐', xpReward: 50 },
  { id: 'ten_wins',       name: '10 Wins',           description: 'Closed 10 winning trades',          icon: '💎', xpReward: 100 },
  { id: 'fifty_trades',   name: '50 Trades',         description: 'Posted 50 trade ideas',             icon: '📊', xpReward: 75 },
  { id: 'hundred_trades', name: '100 Trades',        description: 'Posted 100 trade ideas',            icon: '🎯', xpReward: 150 },
  { id: 'streak_5',       name: '5-Day Streak',      description: 'Active for 5 consecutive days',     icon: '🔥', xpReward: 30 },
  { id: 'top_trader',     name: 'Top Trader',        description: 'Reached the top of the leaderboard',icon: '👑', xpReward: 200 },
  { id: 'battle_winner',  name: 'Battle Winner',     description: 'Won your first Trade Battle',       icon: '⚔️', xpReward: 50 },
  { id: 'verified',       name: 'Verified Trader',   description: 'Earned Verified Trader status',     icon: '✅', xpReward: 100 },
  // Referral v2 — see src/lib/referral.ts and the referral_v2 migration
  { id: 'founding_friend',  name: 'Founding Friend',   description: 'Joined via a friend’s invite',      icon: '🤝', xpReward: 100 },
  { id: 'recruiter_bronze', name: 'Bronze Recruiter',  description: 'Referred 3 qualified traders',         icon: '🥉', xpReward: 500 },
  { id: 'recruiter_silver', name: 'Silver Recruiter',  description: 'Referred 10 qualified traders',        icon: '🥈', xpReward: 2000 },
  { id: 'recruiter_gold',   name: 'Gold Recruiter',    description: 'Referred 25 qualified traders',        icon: '🥇', xpReward: 5000 },
  { id: 'recruiter_legend', name: 'Legend Recruiter',  description: 'Referred 50 qualified traders',        icon: '🏆', xpReward: 10000 },
];

export function getAchievementDef(id: string): AchievementDef | undefined {
  return ACHIEVEMENTS.find((a) => a.id === id);
}
